import React from 'react'
import { usePerformanceKPI } from '../../hooks/useTasks'

interface PerformanceKPICardProps {
  projectId: number
}

export const PerformanceKPICard: React.FC<PerformanceKPICardProps> = ({ projectId }) => {
  const { data: kpi, isLoading, isError } = usePerformanceKPI(projectId)
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }
  
  if (isError || !kpi) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <p className="text-red-500 text-center">Failed to load KPI data</p>
      </div>
    )
  }
  
  const getProgressBar = (value: number, max = 100) => {
    const percentage = Math.min(value, max)
    const bgColor = percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${bgColor} h-2 rounded-full`} style={{ width: `${percentage}%` }}></div>
      </div>
    )
  }
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Performance KPI</h2>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Total Tasks</span>
              <span className="font-medium">{kpi.totalTasks}</span>
            </div>
            {getProgressBar(kpi.completionRate, 100)}
            <p className="text-xs text-gray-500 mt-1">Completion Rate: {kpi.completionRate.toFixed(1)}%</p>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Completed</span>
              <span className="font-medium">{kpi.completedTasks}</span>
            </div>
            {getProgressBar((kpi.completedTasks / kpi.totalTasks) * 100, 100)}
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">In Progress</span>
              <span className="font-medium">{kpi.inProgressTasks}</span>
            </div>
            {getProgressBar((kpi.inProgressTasks / kpi.totalTasks) * 100, 100)}
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Delayed</span>
              <span className="font-medium">{kpi.delayedTasks}</span>
            </div>
            {getProgressBar(kpi.delayRate, 100)}
            <p className="text-xs text-gray-500 mt-1">Delay Rate: {kpi.delayRate.toFixed(1)}%</p>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">On Time</span>
              <span className="font-medium">{kpi.onTimeRate.toFixed(1)}%</span>
            </div>
            {getProgressBar(kpi.onTimeRate, 100)}
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Avg Duration</span>
              <span className="font-medium">{kpi.averageTaskDuration} days</span>
            </div>
            {getProgressBar(kpi.averageTaskDuration, 30)}
          </div>
        </div>
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600 font-medium">Average Progress</span>
          <span className="font-medium">{kpi.averageTaskProgress}%</span>
        </div>
        {getProgressBar(kpi.averageTaskProgress, 100)}
      </div>
    </div>
  )
}

PerformanceKPICard.displayName = 'PerformanceKPICard'